import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { Advertisement } from '../../types/advertisement';
// import { fetchJWT } from '../utils';

interface AdvertisementState {
	currentAdvertisement?: string;
	status: 'idle' | 'loading' | 'succeeded' | 'failed';
	error: string | null;
	successMessage?: string;
}

interface ErrorResponse {
	response: {
		data: {
			error: string;
		};
	};
}

export interface UpdateAdvertisementResponse {
	advertisementCode: string;
}

const initialState: AdvertisementState = {
	currentAdvertisement: undefined,
	status: 'idle',
	error: null,
	successMessage: undefined
};

export const getAdvertisement = createAsyncThunk<Advertisement, undefined, { dispatch: AppDispatch; state: RootState }>(
	'advertisement/getAdvertisement',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.get('inspirusapi', '/user/advertisement', {
				headers: {}
			});

			return { advertisement: response.data.advertisementCode.advertisementCode };
		} catch (error: unknown) {
			if (error instanceof Error && 'response' in error) {
				const errResponse = error as ErrorResponse;
				return rejectWithValue(errResponse.response?.data.error || 'Failed to fetch advertisement.');
			}
			return rejectWithValue('Failed to fetch advertisement.');
		}
	}
);

export const updateAdvertisement = createAsyncThunk<
	UpdateAdvertisementResponse,
	{ advertisementCode: string },
	{ dispatch: AppDispatch; state: RootState }
>('advertisement/updateAdvertisement', async ({ advertisementCode }, { rejectWithValue }) => {
	try {
		await API.put('inspirusapi', '/admins/update-advertisement', {
			headers: {},
			body: { advertisementCode }
		});
		return { advertisementCode: advertisementCode };
	} catch (error: unknown) {
		if (error instanceof Error && 'response' in error) {
			const errResponse = error as ErrorResponse;
			return rejectWithValue(errResponse.response?.data.error || 'Failed to update advertisement.');
		}
		return rejectWithValue('Failed to update advertisement.');
	}
});

const advertisementSlice = createSlice({
	name: 'advertisement',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateAdvertisement.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(updateAdvertisement.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.currentAdvertisement = action.payload.advertisementCode;
			})
			.addCase(updateAdvertisement.rejected, (state, action) => {
				state.error = (action.payload as string) || 'Could not update advertisement';
				state.status = 'failed';
			});
		builder
			.addCase(getAdvertisement.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(getAdvertisement.fulfilled, (state, action) => {
				state.currentAdvertisement = action.payload.advertisement;
				state.status = 'succeeded';
			})
			.addCase(getAdvertisement.rejected, (state, action) => {
				state.error = action.error.message || 'Could not fetch advertisement';
				state.status = 'failed';
			});
	}
});

// You can add more reducers or actions if needed
// export const { } = advertisementSlice.actions;
export default advertisementSlice.reducer;
